import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetOne, useNotify, useRefresh } from 'react-admin';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import type { AnimationSummary } from '@wwf/sdk';
import { AnimationDrawerForm } from './AnimationDrawerForm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerState } from '@app/misc/DrawerContext';
import { useDrawer } from '@app/misc/DrawerContext';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';

type AnimationDrawerMode = 'create' | 'edit' | 'show';

export function useAnimationDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (
            mode: AnimationDrawerMode,
            selectedId: AnimationSummary['id'] | null
        ) =>
            drawer.openDrawer(state => (
                <AnimationDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function AnimationDrawer({
    open,
    onClose,
    mode,
    selectedId,
}: DrawerState & {
    mode: AnimationDrawerMode;
    selectedId: AnimationSummary['id'] | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const [viewMode, setViewMode] = useState<AnimationDrawerMode>(mode);

    useEffect(() => {
        if (open) {
            setViewMode(mode);
        }
    }, [open, mode, selectedId]);

    const title = useMemo(() => {
        if (viewMode === 'create') return 'Create animation';
        if (viewMode === 'show') return 'Animation';
        return 'Edit animation';
    }, [viewMode]);

    const loadRecord = viewMode === 'edit' || viewMode === 'show';

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useGetOne<AnimationSummary>(
            'animation',
            { id: selectedId ?? undefined },
            { enabled: open && loadRecord && !!selectedId }
        );

    const canToggleShowEdit = !!selectedId && viewMode !== 'create';

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                >
                    <Typography variant="h6">{title}</Typography>
                    {canToggleShowEdit &&
                        (viewMode === 'show' ? (
                            <Stack
                                direction={'row'}
                                mb={2}
                                flexWrap={'wrap'}
                                justifyContent={'space-between'}
                                gap={1}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setViewMode('edit')}
                                >
                                    Edit
                                </Button>
                                <CopyAppLinkButton
                                    type="animation"
                                    id={selectedRecord?.id ?? ''}
                                />
                            </Stack>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewMode('show')}
                            >
                                View
                            </Button>
                        ))}
                </Stack>

                <AnimationDrawerForm
                    mode={viewMode}
                    record={loadRecord ? selectedRecord : undefined}
                    loading={loadRecord && isPendingSelectedRecord}
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify('Saved', { type: 'info' });
                    }}
                />
            </Box>
        </Box>
    );
}
